import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Pencil, Play, Plus, Trash2, Workflow } from '@/components/icons';
import { starterGraph, testNameKey } from '@1qa/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { timeAgo } from '@/lib/format';
import type { FlowListItem, Project } from '@/lib/types';
import { useRunCollection } from '@/lib/runCollection';
import { RunCollectionPicker } from '@/components/testdata/RunCollectionPicker';
import { useRunBuild } from '@/lib/runBuild';
import { RunBuildPicker } from '@/components/builds/RunBuildPicker';
import { useRunDevice } from '@/lib/runDevice';
import { RunDevicePicker } from '@/components/builds/RunDevicePicker';
import { Badge, Button, Card, CardHeader, Dialog, EmptyState, ErrorNotice, Field, Input, LinkButton, PageLoader, StatusPill, useConfirm, useToast } from '@/components/ui';

/** The project's flows (diagrams of tests and groups) and their recent runs. */
export function FlowsTab({ project, canWrite, canRun }: { project: Project; canWrite: boolean; canRun: boolean }) {
  const { canModify } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const runData = useRunCollection(project.id);
  const runBuild = useRunBuild(project.id, project.platform);
  const runDevice = useRunDevice(project.id, project.platform);
  const flows = useQuery({ queryKey: ['flows', project.id], queryFn: () => api.flows(project.id) });
  const runs = useQuery({
    queryKey: ['flow-runs', project.id],
    queryFn: () => api.flowRuns(project.id),
    refetchInterval: (q) => (q.state.data?.some((r) => r.status === 'QUEUED' || r.status === 'RUNNING') ? 3000 : false),
  });

  const run = useMutation({
    mutationFn: (f: FlowListItem) => api.runFlow(f.id, { collectionId: runData.collectionId, appBuildId: runBuild.appBuildId, deviceProfile: runDevice.deviceProfile }),
    onSuccess: (r, f) => {
      toast.success(`“${f.name}” started`);
      qc.invalidateQueries({ queryKey: ['flows', project.id] });
      navigate(`/flow-runs/${r.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (f: FlowListItem) => api.deleteFlow(f.id),
    onSuccess: () => {
      toast.success('Flow deleted');
      qc.invalidateQueries({ queryKey: ['flows', project.id] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (flows.isPending) return <PageLoader />;
  if (flows.isError) return <ErrorNotice error={flows.error} onRetry={() => flows.refetch()} />;

  const dialog = creating && <NewFlowDialog project={project} taken={flows.data.map((f) => f.name)} onClose={() => setCreating(false)} />;

  return (
    <div className="space-y-6">
      {dialog}
      {flows.data.length === 0 ? (
        <EmptyState
          icon={<Workflow className="size-6" />}
          title="No flows yet"
          description="Draw how your tests and groups run: start, run things side by side, decide what happens after a failure, and finish."
          action={
            canWrite && (
              <Button onClick={() => setCreating(true)} icon={<Plus className="size-4" />}>
                Create a flow
              </Button>
            )
          }
        />
      ) : (
        <Card>
          <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-3.5 dark:border-slate-800">
            <p className="text-sm text-slate-500">
              {flows.data.length} flow{flows.data.length === 1 ? '' : 's'}
            </p>
            <div className="ml-auto flex flex-wrap items-center gap-3">
              {canRun && <RunBuildPicker state={runBuild} />}
              {canRun && <RunDevicePicker state={runDevice} />}
              {canRun && <RunCollectionPicker state={runData} />}
              {canWrite && (
                <Button variant="outline" onClick={() => setCreating(true)} icon={<Plus className="size-4" />}>
                  New flow
                </Button>
              )}
            </div>
          </div>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {flows.data.map((f) => {
              const mine = canModify('test.write', f.createdById, project.orgId);
              return (
                <li key={f.id} className="flex flex-wrap items-center gap-4 px-5 py-3.5">
                  <span className="grid size-9 place-items-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-900/40 dark:text-brand-300">
                    <Workflow className="size-[18px]" />
                  </span>
                  <Link to={`/projects/${project.id}/flows/${f.id}`} className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-slate-900 hover:text-brand-600 dark:text-white">{f.name}</div>
                    <div className="truncate text-xs text-slate-500">
                      {f.steps} step{f.steps === 1 ? '' : 's'} · updated {timeAgo(f.updatedAt)}
                      {f.createdBy && ` · by ${f.createdBy.name}`}
                    </div>
                  </Link>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {!f.runnable && <Badge tone="warning">Needs work</Badge>}
                    {f.continueOnFailure && <Badge>Continues after failures</Badge>}
                    {f.lastRun && (
                      <Link to={`/flow-runs/${f.lastRun.id}`} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-brand-600" title="Open the last run">
                        <StatusPill status={f.lastRun.status} /> {timeAgo(f.lastRun.finishedAt ?? f.lastRun.queuedAt)}
                      </Link>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {canRun && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => run.mutate(f)}
                        loading={run.isPending && run.variables?.id === f.id}
                        disabled={!f.runnable}
                        title={f.runnable ? undefined : 'Open the flow to finish it first'}
                        icon={<Play className="size-4" />}
                      >
                        Run flow
                      </Button>
                    )}
                    <LinkButton
                      size="sm"
                      variant="ghost"
                      to={`/projects/${project.id}/flows/${f.id}`}
                      aria-label={`${mine ? 'Edit' : 'View'} ${f.name}`}
                      icon={mine ? <Pencil className="size-4" /> : <Eye className="size-4" />}
                    />
                    {mine && (
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Delete ${f.name}`}
                        onClick={async () => {
                          const ok = await confirm({ title: 'Delete this flow?', message: `“${f.name}” will be deleted. Its tests, groups and past runs stay.`, confirmLabel: 'Delete flow' });
                          if (ok) remove.mutate(f);
                        }}
                        icon={<Trash2 className="size-4 text-rose-500" />}
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {runs.data && runs.data.length > 0 && (
        <Card>
          <CardHeader title="Recent flow runs" />
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {runs.data.slice(0, 10).map((r) => (
              <li key={r.id}>
                <Link to={`/flow-runs/${r.id}`} className="flex items-center gap-3 px-5 py-2.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <StatusPill status={r.status} />
                  <span className="min-w-0 flex-1 truncate font-medium">{r.flowName}</span>
                  {r.collectionName && <Badge tone="info">{r.collectionName}</Badge>}
                  <span className="text-xs text-slate-500">
                    {r.triggeredBy?.name && `${r.triggeredBy.name} · `}
                    {timeAgo(r.finishedAt ?? r.startedAt ?? r.queuedAt)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function NewFlowDialog({ project, taken, onClose }: { project: Project; taken: string[]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const clash = !!name.trim() && taken.some((t) => testNameKey(t) === testNameKey(name));
  const create = useMutation({
    mutationFn: () => api.createFlow(project.id, { name: name.trim(), continueOnFailure: false, graph: starterGraph() }),
    onSuccess: (f) => {
      qc.invalidateQueries({ queryKey: ['flows', project.id] });
      navigate(`/projects/${project.id}/flows/${f.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim() && !clash) create.mutate();
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title="New flow"
      description="It starts with a Start and an End; add tests, groups and conditions in between."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="new-flow" loading={create.isPending} disabled={!name.trim() || clash}>
            Create and open
          </Button>
        </>
      }
    >
      <form id="new-flow" onSubmit={submit}>
        <Field label="Name" error={clash ? `A flow named “${name.trim()}” already exists in this project.` : undefined}>
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Nightly regression" maxLength={200} autoFocus />}
        </Field>
      </form>
    </Dialog>
  );
}
